import type React from 'react';
import { useCallback, useState, useEffect } from 'react';

import { DriveEtaRounded, ImageRounded } from '@mui/icons-material';
import { Alert, Paper, Typography } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { trackAnalyticsEvent } from '@ragu-components/ord-analytics';
import { OrdFormList, OrdReferenceInput, UserPhotoDropzone } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { CallflowReadFields, UserReadFields, UserUpdateFields } from '@ragu-components/ord-types';
import { SimpleForm, useNotify, SaveContextProvider, useTranslate, ResourceContextProvider } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { SimpleSaveToolbar } from 'src/components/forms';

import type { UserSettingsRead, UserSettingsUpdate } from '@ragu-components/ord-types';
import type { TextInputProps } from 'react-admin';
import type { FieldValues } from 'react-hook-form';

export const UserSettings = (): React.ReactElement => {
  const dataProvider = useIngordDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  const [saving, setSaving] = useState(false);

  const [userSettings, setUserSettings] = useState<UserSettingsRead | null>(null);

  useEffect(() => {
    const fetchUserSettings = async (): Promise<void> => {
      try {
        const data = await dataProvider.getUserSettings();
        setUserSettings(data);
        componentLogger.info('User Settings fetched', { data });
      } catch (error) {
        componentLogger.error('User Settings fetch failed', { error });
        notify('ord.settings.userSettings.requestError', { type: 'error' });
      }
    };
    if (!userSettings) {
      void fetchUserSettings();
    }
  }, [dataProvider, notify, userSettings]);

  const handleSave = useCallback(
    (values: FieldValues) => {
      if (!userSettings) {
        componentLogger.debug('No User Settings');
        return;
      }

      const updates: UserSettingsUpdate = {};

      // Handle mobility configuration update
      const mobilityConfiguration = values[UserReadFields.mobility_configuration];
      if (mobilityConfiguration !== undefined) {
        updates.mobility_configuration = mobilityConfiguration;
      }

      // Handle photo update
      const photo = values[UserUpdateFields.photo];
      if (photo !== undefined && photo !== userSettings.photo) {
        updates.photo = photo;
      }

      // If nothing to update, return
      if (Object.keys(updates).length === 0) {
        componentLogger.debug('No updates to save');
        return;
      }

      setSaving(true);
      dataProvider
        .updateUserSettings(updates)
        .then(() => {
          componentLogger.info('User settings updated', { updates });
          trackAnalyticsEvent('user_settings_saved', {
            event_category: 'settings',
            value: Object.keys(updates).length,
          });
          setUserSettings(null);
          notify('ord.settings.userSettings.updateSuccess', { type: 'info' });
        })
        .catch(() => {
          componentLogger.error('User settings update failed');
          trackAnalyticsEvent('user_settings_save_failed', { event_category: 'settings' });
          notify('ord.settings.userSettings.updateError', { type: 'error' });
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [dataProvider, notify, userSettings]
  );

  const saveContext = {
    save: handleSave,
    saving,
  };

  const toTextInputElement = (element: React.ReactElement): React.ReactElement<TextInputProps> => {
    return element as unknown as React.ReactElement<TextInputProps>;
  };

  const mobilityWarning = toTextInputElement(
    <Alert severity='warning' sx={{ mb: 2 }}>
      {translate('ord.settings.userSettings.mobilityWarning')}
    </Alert>
  );

  const mobilityAssigned = toTextInputElement(
    userSettings?.mobility_configuration !== null && userSettings?.mobility_configuration !== undefined ? (
      <Typography sx={{ mb: 1 }}>
        {translate('ord.settings.userSettings.assignedMobility')}: <b>{userSettings.mobility_configuration?.name}</b>
      </Typography>
    ) : (
      <span />
    )
  );

  const mobilityInput = toTextInputElement(
    <OrdReferenceInput
      disabled={saving}
      resource={`me/${Resources.mobility_configurations}`}
      source={UserReadFields.mobility_configuration}
      label='resources.users.fields.mobility_configuration'
      filter={{
        active: 'fiql',
        fiql: `(${CallflowReadFields.latest_callflow_version}.id=isnull=true)`,
      }}
    />
  );

  return (
    <SaveContextProvider value={saveContext}>
      {userSettings && (
        <ResourceContextProvider value={Resources.users}>
          <Paper sx={{ overflow: 'hidden' }}>
            <SimpleForm
              record={{
                [UserReadFields.mobility_configuration]: userSettings.mobility_configuration?.id ?? null,
                [UserUpdateFields.photo]: userSettings.photo,
              }}
              onSubmit={handleSave}
              toolbar={<SimpleSaveToolbar />}
            >
              <OrdFormList icon={<DriveEtaRounded />}>{[mobilityWarning, mobilityAssigned, mobilityInput]}</OrdFormList>
              <OrdFormList icon={<ImageRounded />}>
                <Typography sx={{ my: 1 }}>{translate('resources.users.fields.photo.name')}</Typography>
                <UserPhotoDropzone source={UserUpdateFields.photo} />
              </OrdFormList>
            </SimpleForm>
          </Paper>
        </ResourceContextProvider>
      )}
    </SaveContextProvider>
  );
};
